<template>
  <v-container v-if="playlist">
    <v-row>
      <v-col cols="auto">
        <v-btn
          icon
          :to="{ name: 'playlists' }"
          exact
        >
          <v-icon small>
            fas fa-arrow-left
          </v-icon>
        </v-btn>
      </v-col>
      <v-col>
        <div class="title text-uppercase font-weight-medium">
          Playlist &middot; <strong>{{ playlist.title }}</strong>
        </div>
      </v-col>
      <v-col
        cols="auto"
        class="body-2 pt-5"
      >
        Last Update: {{ $dayjs(playlist.updated_at).format('DD.MM.YYYY. HH:mm:ss') }}
      </v-col>
      <v-col cols="auto">
        <v-btn
          v-if="playlist.is_child || playlist.is_handpicked"
          rounded
          outlined
          small
          color="primary"
          class="mr-2 text-none"
          :to="{ name: 'musicLibrary', query: { add_playlist_id: playlist.id }}"
        >
          <v-icon
            x-small
            left
          >
            fa fa-plus
          </v-icon>
          Add songs
        </v-btn>
        <v-btn
          v-if="!playlist.is_handpicked && !playlist.is_child"
          rounded
          outlined
          small
          color="primary"
          class="mr-2 text-none"
          :to="{ name: 'musicLibrary', query: { attributes: attributesForMusicLibrary, upload: true }}"
        >
          <v-icon
            x-small
            left
          >
            fa fa-plus
          </v-icon>
          Upload
        </v-btn>
        <v-tooltip
          v-if="!playlist.is_child"
          top
        >
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              @click="editPlaylistDialog.clone = true; editPlaylistDialog.visible = true"
            >
              <v-icon small>
                far fa-clone
              </v-icon>
            </v-btn>
          </template>
          <span>
            Create child playlist
          </span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              @click="editPlaylistDialog.clone = false; editPlaylistDialog.visible = true"
            >
              <v-icon small>
                fa fa-pen
              </v-icon>
            </v-btn>
          </template>
          <span>
            Edit playlist
          </span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              @click="confirmDeleteDialog.visible = true"
            >
              <v-icon small>
                fa fa-trash
              </v-icon>
            </v-btn>
          </template>
          <span>
            Delete playlist
          </span>
        </v-tooltip>
      </v-col>
    </v-row>

    <div
      class="pa-3 caption mb-2 rounded"
      style="border: 1px solid rgba(0, 0, 0, 0.12)"
    >
      <v-row dense>
        <v-col
          v-if="playlist.is_child"
          cols="auto"
        >
          <v-chip
            small
            color="secondary"
            dark
            :to="{ name: 'playlist', params: { id: playlist.parent.id }}"
          >
            Child of
            {{ playlist.parent.title }}
            <v-icon
              right
              x-small
            >
              fa fa-external-link-alt
            </v-icon>
          </v-chip>
        </v-col>
        <v-col
          v-if="!playlist.is_child"
          cols="auto"
        >
          <v-chip
            small
          >
            {{ playlist.is_handpicked ? 'Songs handpicked' : 'Songs by Attributes' }}
          </v-chip>
        </v-col>
        <v-col
          cols="auto"
        >
          <span class="caption">Client:</span>
          <v-chip
            small
            class="ml-1"
          >
            {{ playlist.client ? playlist.client.name : 'All Clients' }}
          </v-chip>
        </v-col>
        <v-col
          v-if="!playlist.is_child && !playlist.is_handpicked"
          cols="auto"
        >
          <span class="caption mr-1">Attributes:</span>
          <v-chip
            v-for="value in playlist.attribute_values"
            :key="value.id"
            small
            class="mr-1"
          >
            {{ value.value }}
          </v-chip>
        </v-col>
        <v-col
          v-if="playlist.children.length > 0"
          cols="auto"
        >
          <span class="caption mr-1">Child playlists:</span>
          <v-chip
            v-for="cp in playlist.children"
            :key="cp.id"
            small
            class="mr-1"
            :to="{ name: 'playlist', params: { id: cp.id }}"
          >
            {{ cp.title }}
            <v-icon
              right
              x-small
            >
              fa fa-external-link-alt
            </v-icon>
          </v-chip>
        </v-col>
        <!--          <v-chip small>-->
        <!--            <strong class="mr-1">Content:</strong>-->
        <!--            {{ playlist.is_child ? 'from parent' : (playlist.)}}-->
        <!--          </v-chip>-->
      </v-row>
      <div class="px-1 pt-2">
        {{ playlist.description }}
      </div>
    </div>

    <div
      v-if="playlist.is_child"
      class="mt-6"
    >
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          <v-subheader class="font-weight-bold pl-2">
            Child Playlist Changes
          </v-subheader>
        </v-col>
        <v-col cols="auto">
          <v-btn
            small
            class="text-none ma-0"
            :color="changedLibraryFiles.filter === 'added' ? 'secondary' : undefined"
            :text="changedLibraryFiles.filter !== 'added'"
            @click="changedLibraryFiles.filter === 'added' ? changedLibraryFiles.filter = undefined : changedLibraryFiles.filter = 'added'"
          >
            Added on top of parent
          </v-btn>
          <v-btn
            small
            class="text-none"
            ma-0
            :color="changedLibraryFiles.filter === 'removed' ? 'secondary' : undefined"
            :text="changedLibraryFiles.filter !== 'removed'"
            @click="changedLibraryFiles.filter === 'removed' ? changedLibraryFiles.filter = undefined : changedLibraryFiles.filter = 'removed'"
          >
            Removed from parent
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        :headers="childPlaylistHeaders"
        :items="changedLibraryFilesFiltered"
        :loading="changedLibraryFiles.loading"
        hide-default-footer
        disable-pagination
        class="v-data-table--striped elevation-15"
      >
        <template v-slot:item="{ item: file }">
          <tr>
            <td style="width: 1px">
              <v-chip
                x-small
                dark
                :color="file.status === 'added' ? 'blue' : 'red'"
              >
                {{ file.status }}
              </v-chip>
            </td>
            <td>
              {{ file.artist }}
            </td>
            <td>
              {{ file.title }}
            </td>
            <td>
              <div v-if="file.attribute_values.length > 0">
                <v-chip
                  v-for="value in file.attribute_values"
                  :key="value.id"
                  x-small
                  outlined
                  class="mr-1"
                >
                  {{ value.value }}
                </v-chip>
              </div>
              <div
                v-else
                class="text--disabled caption"
              >
                None
              </div>
            </td>
            <td style="width: 1px">
              <audio
                :key="'audio-player-' + file.id"
                style="height: 26px"
                controls=""
                preload="none"
              >
                <source
                  :src="file.url"
                  type="audio/mpeg"
                >
              </audio>
            </td>
            <td style="width: 1px">
              <v-btn
                v-if="file.status === 'added'"
                small
                outlined
                rounded
                class="text-none"
                @click="removeFile(file)"
              >
                <v-icon
                  left
                  x-small
                >
                  fa fa-trash
                </v-icon>
                Remove
              </v-btn>
              <v-btn
                v-else
                small
                outlined
                rounded
                class="text-none"
                @click="removeFile(file)"
              >
                <v-icon
                  left
                  x-small
                >
                  fa fa-undo
                </v-icon>
                Add back
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <div>
      <v-subheader
        v-if="playlist.is_child"
        class="font-weight-bold mt-12 pl-2"
      >
        Parent Playlist Songs &middot; {{ playlist.parent.title }}
      </v-subheader>
      <pagination
        :page.sync="items.pagination.page"
        :last-page="items.meta.last_page"
        :total="items.meta.total"
        :from="items.meta.from"
        :to="items.meta.to"
        :total-visible="8"
      />
      <v-data-table
        :headers="headers"
        :items="items.data"
        :server-items-length="items.meta.total"
        :sort-by.sync="items.sorting.sortBy"
        :sort-desc.sync="items.sorting.desc"
        :page.sync="items.pagination.page"
        :items-per-page="items.pagination.perPage"
        :loading="items.loading"
        hide-default-footer
        must-sort
        class="v-data-table--striped elevation-15"
        @update:sort-by="fetchItems()"
        @update:sort-desc="fetchItems()"
        @update:page="fetchItems()"
      >
        <template v-slot:item="{ item: file }">
          <tr>
            <td>
              {{ file.artist }}
            </td>
            <td>
              {{ file.title }}
            </td>
            <td>
              <div v-if="file.attribute_values.length > 0">
                <v-chip
                  v-for="value in file.attribute_values"
                  :key="value.id"
                  x-small
                  outlined
                  class="mr-1"
                >
                  {{ value.value }}
                </v-chip>
              </div>
              <div
                v-else
                class="text--disabled caption"
              >
                None
              </div>
            </td>
            <td style="width: 1px">
              <audio
                :key="'audio-player-' + file.id"
                style="height: 26px"
                controls=""
                preload="none"
              >
                <source
                  :src="file.url"
                  type="audio/mpeg"
                >
              </audio>
            </td>
            <td style="width: 1px">
              <v-btn
                v-if="playlist.is_handpicked || playlist.is_child"
                small
                outlined
                rounded
                :disabled="playlist.is_child && changedLibraryFiles.data.find(f => f.id === file.id && f.status === 'removed')"
                class="text-none"
                @click="removeFile(file)"
              >
                <v-icon
                  left
                  x-small
                >
                  fa fa-trash
                </v-icon>
                {{ playlist.is_child && changedLibraryFiles.data.find(f => f.id === file.id && f.status === 'removed') ? 'Removed' : 'Remove' }}
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
      <pagination
        :page.sync="items.pagination.page"
        :last-page="items.meta.last_page"
        :total="items.meta.total"
        :from="items.meta.from"
        :to="items.meta.to"
      />
    </div>

    <edit-playlist-dialog
      :playlist="editPlaylistDialog.clone ? undefined : playlist"
      :parent-playlist="editPlaylistDialog.clone ? playlist : undefined"
      :visible.sync="editPlaylistDialog.visible"
      @playlist:updated="fetchPlaylist()"
      @playlist:created="onCreated"
    />

    <confirm-delete-dialog
      :visible.sync="confirmDeleteDialog.visible"
      :submitting="confirmDeleteDialog.deleting"
      @confirm="deletePlaylist()"
    />
  </v-container>
</template>

<script>
// @ is an alias to /src
import Playlists from '@/services/Playlists'
import Pagination from '@/components/helpers/Pagination'
import fetchableItemsMixin from '@/mixins/fetchableItemsMixin'
import LibraryFiles from '@/services/LibraryFiles'
import LibraryFileModel from '@/models/LibraryFileModel'
import EditPlaylistDialog from '@/components/playlists/EditPlaylistDialog'
import ConfirmDeleteDialog from '../components/helpers/ConfirmDeleteDialog'

export default {
  name: 'Playlist',
  components: {
    ConfirmDeleteDialog,
    EditPlaylistDialog,
    Pagination
  },
  mixins: [fetchableItemsMixin],
  data() {
    return {
      playlist: undefined,
      filters: {},
      editPlaylistDialog: {
        visible: false,
        clone: false
      },
      changedLibraryFiles: {
        loading: false,
        data: [],
        filter: undefined
      },
      confirmDeleteDialog: {
        visible: false,
        deleting: false
      }
    }
  },
  computed: {
    changedLibraryFilesFiltered() {
      return this.changedLibraryFiles.data.filter(f => {
        return !this.changedLibraryFiles.filter || (this.changedLibraryFiles.filter === f.status)
      })
    },
    attributesForMusicLibrary() {
      if (!this.playlist) return undefined

      const attributes = {}

      for (const attributeValue of this.playlist.attribute_values) {
        if (!Array.isArray(attributes[attributeValue.attribute.id])) {
          attributes[attributeValue.attribute.id] = [attributeValue.id]
        } else {
          attributes[attributeValue.attribute.id].push(attributeValue.id)
        }
      }

      return attributes
    }
  },
  watch: {
    '$route.params.id': function () {
      this.fetchPlaylist()
    }
  },
  created() {
    this.headers = [
      { text: 'Artist', value: 'artist' },
      { text: 'Title', value: 'title' },
      { text: 'Attributes', sortable: false },
      { text: 'Player', sortable: false },
      { sortable: false }
    ]

    this.childPlaylistHeaders = [
      { sortable: false },
      { text: 'Artist', value: 'artist' },
      { text: 'Title', value: 'title' },
      { text: 'Attributes', sortable: false },
      { text: 'Player', sortable: false },
      { sortable: false }
    ]

    this.fetchPlaylist()
  },
  methods: {
    async itemsFetcher(queryParams) {
      if (!this.playlist) {
        return {
          data: [],
          meta: {}
        }
      }

      return await LibraryFiles.fetch({
        ...queryParams,
        type: LibraryFileModel.TYPE_MUSIC,
        playlists: [this.playlist.is_child ? this.playlist.parent.id : this.playlist.id]
      })
    },
    async fetchPlaylist() {
      try {
        this.playlist = await Playlists.get(this.$route.params.id)

        this.$nextTick(() => {
          this.fetchItems()
          this.fetchChildPlaylistChangedFiles()
        })
      } catch (e) {
        console.error(e)
      }
    },
    async deletePlaylist() {
      this.confirmDeleteDialog.deleting = true

      try {
        await Playlists.delete([this.playlist.id])

        this.$store.dispatch('alert', {
          text: 'Playlist deleted',
          color: 'success'
        })

        this.$router.push({ name: 'playlists' })
      } catch (e) {
        if (e.response?.data?.error === 'forbidden') {
          this.$store.dispatch('alert', {
            text: 'Playlist can not be deleted due to active clocks or child playlists. Delete those first.',
            color: 'error'
          })

          return
        }

        this.$store.dispatch('alert', {
          text: 'Error deleting playlist',
          color: 'error'
        })
        throw e
      } finally {
        this.confirmDeleteDialog.deleting = false
      }
    },
    onCreated(playlist) {
      this.$router.push({ name: 'playlist', params: { id: playlist.id } })
    },
    async fetchChildPlaylistChangedFiles() {
      if (!this.playlist || !this.playlist.is_child) return

      this.changedLibraryFiles.loading = true

      try {
        // first include added files
        let files = (await LibraryFiles.fetch({ playlists: [this.playlist.id] })).map(f => ({ ...f, status: 'added' }))

        // include removed files
        const removedFiles = (await Playlists.fetchDisabledFiles(this.playlist.id)).map(f => ({ ...f, status: 'removed' }))

        files = [...files, ...removedFiles]

        this.changedLibraryFiles.data = files
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Error getting songs',
          color: 'error'
        })
        throw e
      } finally {
        this.changedLibraryFiles.loading = false
      }
    },
    async removeFile(file) {
      try {
        if ((!file.status || file.status === 'removed') && this.playlist.is_child) {
          await Playlists.toggleFileDisabled(this.playlist.id, file.id)

          if (file.status === 'removed') {
            this.$store.dispatch('alert', {
              text: 'Song reverted back to list',
              color: 'success'
            })
          } else {
            this.$store.dispatch('alert', {
              text: 'Song removed',
              color: 'success'
            })
          }
        } else {
          await Playlists.removeFilesFromPlaylist(this.playlist.id, [file.id])

          this.$store.dispatch('alert', {
            text: 'Song removed',
            color: 'success'
          })
        }

        this.fetchPlaylist()
        this.fetchItems()
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Song removed',
          color: 'success'
        })
      }
    }
  }
}
</script>
